/**
 * Admin → Egress log
 *
 * Phase 5b item 8 (2026-05-16). Surfaces every metadata payload that
 * has left the customer's network, with the SHA-256 hash-chain and a
 * one-click chain-verification button. Mirrors the contract the
 * CISO uses for tamper-evidence audits.
 *
 * Backend:
 *   GET /v1/foundational/egress-log          → list with cursor pagination
 *   GET /v1/foundational/egress-log/verify   → re-walks chain, reports
 *                                              valid/firstBreakAt
 *
 * Why this page matters for demo: after the cross-repo wire test (Entry
 * #52) we promised the CISO a place to see "did anything leave my
 * network unexpectedly?" - this is that view. Empty state is OK and
 * expected on a fresh tenant; the verify button still passes because
 * an empty chain is trivially valid.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  Filter,
  Hash,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listEgressLog,
  getEgressLogSummary,
  verifyEgressLog,
  type EgressLogEntry,
  type EgressPayloadKind,
} from '@/lib/api';

type KindFilter = EgressPayloadKind | 'all';

const KIND_LABELS: Record<EgressPayloadKind, string> = {
  discovery_finding: 'Discovery finding',
  activity_event_summary: 'Activity event summary',
  ddr_decision: 'DDR decision',
  health_check: 'Health check',
  attestation_doc: 'Attestation doc',
};

const KIND_OPTIONS: ReadonlyArray<{ value: KindFilter; label: string }> = [
  { value: 'all', label: 'All kinds' },
  { value: 'discovery_finding', label: 'Discovery findings' },
  { value: 'activity_event_summary', label: 'Activity summaries' },
  { value: 'ddr_decision', label: 'DDR decisions' },
  { value: 'health_check', label: 'Health checks' },
  { value: 'attestation_doc', label: 'Attestation docs' },
];

interface VerifyState {
  status: 'idle' | 'running' | 'ok' | 'broken' | 'error';
  rowsVerified: number;
  firstBreakAt: string | null;
  message: string;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function shortHash(h: string): string {
  return h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-4)}` : h;
}

export function AdminEgressLog(): JSX.Element {
  const [entries, setEntries] = useState<EgressLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof getEgressLogSummary>> | null>(null);
  const [verify, setVerify] = useState<VerifyState>({
    status: 'idle',
    rowsVerified: 0,
    firstBreakAt: null,
    message: '',
  });

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [result, sum] = await Promise.all([
        listEgressLog(kindFilter === 'all' ? { limit: 200 } : { limit: 200, payloadKind: kindFilter }),
        getEgressLogSummary(kindFilter === 'all' ? undefined : kindFilter),
      ]);
      setEntries(result.entries);
      setSummary(sum);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load egress log');
    } finally {
      setLoading(false);
    }
  }, [kindFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleVerify(): Promise<void> {
    setVerify({ status: 'running', rowsVerified: 0, firstBreakAt: null, message: '' });
    try {
      const r = await verifyEgressLog();
      setVerify({
        status: r.valid ? 'ok' : 'broken',
        rowsVerified: r.rowsVerified,
        firstBreakAt: r.firstBreakAt,
        message: r.message,
      });
    } catch (e) {
      setVerify({
        status: 'error',
        rowsVerified: 0,
        firstBreakAt: null,
        message: e instanceof Error ? e.message : 'verification failed',
      });
    }
  }

  // Counted by the server over every row matching the filter (release 2.1
  // item 4); these cards used to count and sum the 200 rows loaded below.
  const stats = useMemo(
    () => ({
      totalBytes: summary?.totalBytes ?? 0,
      byKind: new Map(Object.entries(summary?.byKind ?? {})) as Map<EgressPayloadKind, number>,
      byDest: new Map(Object.entries(summary?.byDestination ?? {})),
    }),
    [summary],
  );

  const verifyBadge = (() => {
    switch (verify.status) {
      case 'ok':
        return <Badge variant="success" className="text-[10px]">CHAIN VALID</Badge>;
      case 'broken':
        return <Badge variant="critical" className="text-[10px]">CHAIN BROKEN</Badge>;
      case 'error':
        return <Badge variant="warning" className="text-[10px]">VERIFY FAILED</Badge>;
      case 'running':
        return <Badge variant="glass" className="text-[10px]">VERIFYING…</Badge>;
      default:
        return null;
    }
  })();

  return (
    <PageContainer>
      <PageHeader
        title="Egress log"
        subtitle="Every metadata payload that has left the customer network, hash-chained for tamper-evidence."
        badges={[
          { label: 'Tamper-evident', variant: 'success' },
          { label: 'CISO audit', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex items-center gap-2">
            {verifyBadge}
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              data-action="disc.adm_egress.a01"
              size="sm"
              onClick={() => void handleVerify()}
              disabled={verify.status === 'running'}
            >
              {verify.status === 'running'
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
              Verify chain
            </Button>
          </div>
        }
      />

      {error && (
        <ErrorState
          title="Failed to load egress log"
          detail={error}
          onRetry={() => void refresh()}
        />
      )}

      {verify.status === 'ok' && (
        <div className="glass-lite border border-success/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">Chain verified - {verify.rowsVerified.toLocaleString('en-IN')} rows</div>
            <div className="text-muted-foreground mt-0.5">{verify.message}</div>
          </div>
        </div>
      )}

      {verify.status === 'broken' && (
        <div className="glass-lite border border-sev-critical/60 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-sev-critical shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">Chain integrity broken</div>
            <div className="text-muted-foreground mt-0.5">
              {verify.message}
              {verify.firstBreakAt && (
                <> · first break at <code className="font-mono">{verify.firstBreakAt}</code></>
              )}
            </div>
          </div>
        </div>
      )}

      {verify.status === 'error' && (
        <div className="glass-lite border border-warning/40 rounded-md p-3 mb-3 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">Verification request failed</div>
            <div className="text-muted-foreground mt-0.5">{verify.message}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Egress rows (filtered)"
          value={summary?.rows ?? 0}
          icon={Hash}
          hint="rows currently shown"
        />
        <KpiCard
          label="Bytes egressed"
          value={stats.totalBytes}
          icon={ExternalLink}
          hint={formatBytes(stats.totalBytes)}
        />
        <KpiCard
          label="Distinct destinations"
          value={summary?.distinctDestinations ?? 0}
          icon={Filter}
          hint="hosts receiving"
        />
        <KpiCard
          label="Chain status"
          value={verify.status === 'ok' ? 1 : verify.status === 'broken' ? 0 : verify.rowsVerified}
          icon={ShieldCheck}
          glow={verify.status === 'ok' ? 'high' : verify.status === 'broken' ? 'critical' : 'medium'}
          hint={
            verify.status === 'ok'
              ? `${verify.rowsVerified.toLocaleString('en-IN')} rows verified`
              : verify.status === 'broken'
                ? 'chain broken - see banner'
                : 'click Verify to check'
          }
        />
      </div>

      <GlassCard className="p-6 mb-4">
        <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Egress entries</CardTitle>
            <CardDescription clamp>
              Newest first. Hash chain links every row to its predecessor - a CISO can replay the chain
              against the genesis seed to prove no row was edited, inserted, or deleted.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as KindFilter)}
              className="glass-lite text-xs rounded px-2 py-1 bg-background/40 border border-border/40"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </CardHeader>

        {loading && <LoadingState caption="Loading egress entries…" />}

        {!loading && entries.length === 0 && (
          <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
            <ShieldCheck className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <div className="font-medium text-foreground/90">No egress rows on file</div>
            <div className="mt-1">
              {kindFilter === 'all'
                ? 'Nothing has left the customer network yet - or the egress writer hasn’t recorded anything for this tenant.'
                : `No ${KIND_LABELS[kindFilter as EgressPayloadKind]?.toLowerCase() ?? kindFilter} entries; try “All kinds”.`}
            </div>
            <div className="mt-3 text-xs">
              An empty chain is still verifiable - click <strong>Verify chain</strong>; the server reports
              <code className="font-mono mx-1">rowsVerified=0, valid=true</code>.
            </div>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs tabular">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border/30">
                  <th className="py-2 pr-3 font-normal">When</th>
                  <th className="py-2 pr-3 font-normal">Kind</th>
                  <th className="py-2 pr-3 font-normal">Destination</th>
                  <th className="py-2 pr-3 font-normal">Size</th>
                  <th className="py-2 pr-3 font-normal">Schema</th>
                  <th className="py-2 pr-3 font-normal">Payload hash</th>
                  <th className="py-2 pr-3 font-normal">Chain hash</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <motion.tr
                    key={e.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.01, 0.4) }}
                    className="border-b border-border/15 hover:bg-muted/20"
                  >
                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                      {new Date(e.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant="glass" className="text-[10px]">
                        {KIND_LABELS[e.payloadKind] ?? e.payloadKind}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <code className="font-mono text-[11px]">
                        {(() => {
                          try {
                            return new URL(e.destinationUrl).host;
                          } catch {
                            return e.destinationUrl;
                          }
                        })()}
                      </code>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{formatBytes(e.payloadSizeBytes)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px]">{e.schemaVersion}</td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground" title={e.payloadHash}>
                      {shortHash(e.payloadHash)}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-primary" title={e.chainHash}>
                      {shortHash(e.chainHash)}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {stats.byDest.size > 0 && (
        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Destinations</CardTitle>
            <CardDescription clamp>
              Where payloads went. A short list is expected - FOCTTA core + the customer’s SIEM/SOAR
              targets. Any unfamiliar host here is worth a security review.
            </CardDescription>
          </CardHeader>
          <div className="space-y-2">
            {[...stats.byDest.entries()]
              .sort((a, b) => b[1] - a[1])
              .map(([host, count], i) => (
                <motion.div
                  key={host}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="flex items-center justify-between gap-3"
                >
                  <code className="font-mono text-xs truncate">{host}</code>
                  <span className="text-xs tabular text-muted-foreground shrink-0">
                    {count.toLocaleString('en-IN')} rows
                  </span>
                </motion.div>
              ))}
          </div>
        </GlassCard>
      )}
    </PageContainer>
  );
}
